/**
 * The circle ✕ on every Beginner Mode screen in a game. It
 * sits on the top-right corner of the screen's own box (its `[role="dialog"]`), kept on screen
 * (full-screen intros get it in the corner). It is drawn on the page body, so a box that scrolls
 * its content never clips it, but follows the box every frame. It stays hidden until the screen
 * has finished coming in (every finite animation and transition on the box, inside it and on
 * its backdrop done, and the box fully opaque, for `SETTLE_MS`), then fades in. Once shown it
 * stays (later steps animating inside the screen don't hide it). In a game it skips every
 * Beginner screen left this game.
 */
import { useLayoutEffect, useRef, useState, type CSSProperties, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { t } from '@kadice/shared';

/** How far the ✕ hangs past the box's corner, and how close it may come to the screen's edge, in px. */
const OUT = 8;
const EDGE = 8;
/** The screen must have been still this long before the ✕ shows. */
const SETTLE_MS = 150;

/** Anything still playing on `el`, inside it or on an ancestor (its backdrop); endless loops (a pulse) don't count. */
function arriving(el: Element): boolean {
  return document.getAnimations().some((a) => {
    if (a.playState !== 'running' && !a.pending) return false;
    const target = (a.effect as KeyframeEffect | null)?.target;
    if (!target || !(target.contains(el) || el.contains(target))) return false;
    return Number.isFinite(a.effect?.getComputedTiming().endTime ?? Infinity);
  });
}

export function WithCloseX({ onClose, children }: { onClose: () => void; children: ReactNode }) {
  const ref = useRef<HTMLDivElement>(null);
  const button = useRef<HTMLButtonElement>(null);
  const [dialog, setDialog] = useState<Element | null>(null);
  const [place, setPlace] = useState<CSSProperties | null>(null);

  // Every render: the screen may mount its dialog late, or swap it (guarded: no loop).
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useLayoutEffect(() => {
    const found = ref.current?.querySelector('[role="dialog"]') ?? null;
    if (found !== dialog) setDialog(found);
  });

  useLayoutEffect(() => {
    if (!dialog) return;
    let frame = 0;
    let stillSince: number | null = null;
    let ready = false;
    const measure = (now: number) => {
      const box = dialog.getBoundingClientRect();
      const size = button.current?.offsetWidth ?? 32;
      const top = Math.round(Math.max(EDGE, box.top - OUT));
      const left = Math.round(Math.min(window.innerWidth - EDGE - size, box.right - size + OUT));
      if (!ready) {
        // The box's own opacity times its ancestors' (a fading backdrop).
        let opacity = 1;
        for (let n: Element | null = dialog; n && n !== document.body; n = n.parentElement) {
          opacity *= parseFloat(getComputedStyle(n).opacity) || 0;
        }
        const still =
          box.width > 0 && getComputedStyle(dialog).visibility !== 'hidden' && opacity > 0.99 && !arriving(dialog);
        stillSince = still ? (stillSince ?? now) : null;
        ready = stillSince !== null && now - stillSince >= SETTLE_MS;
      }
      setPlace((p) =>
        p && p.top === top && p.left === left && p.opacity === (ready ? 1 : 0)
          ? p
          : { top, left, opacity: ready ? 1 : 0, visibility: ready ? 'visible' : 'hidden' },
      );
      frame = requestAnimationFrame(measure);
    };
    frame = requestAnimationFrame(measure);
    return () => cancelAnimationFrame(frame);
  }, [dialog]);

  return (
    <div ref={ref} className="with-close">
      {children}
      {dialog &&
        createPortal(
          <button
            ref={button}
            className="tutorial-close"
            style={place ?? { visibility: 'hidden' }}
            onClick={onClose}
            aria-label={t('common.close')}
            title={t('common.close')}
          >
            ✕
          </button>,
          document.body,
        )}
    </div>
  );
}
